/**
 * Archivos de imagen que el usuario elige en SU equipo (subidas del dashboard).
 *
 * El backend no tiene parser de multipart: las fotos viajan en base64 dentro de un JSON
 * (`imageBase64` + `mimeType`), lo mismo la de la guía de envío (módulo 7) que las del catálogo
 * (módulo 3). Aquí vive lo único que hace falta en el navegador para preparar ese body, para no
 * duplicarlo en los dos módulos:
 *  - `readFileAsDataUrl` → lee el archivo (`FileReader`; solo funciona en el navegador);
 *  - `base64FromDataUrl` → parte el data URL en contenido + MIME;
 *  - `validateImageFile` y sus topes (`IMAGE_MAX_BYTES`, `IMAGE_MIME_TYPES`) → la validación previa
 *    a leer, compartida por la foto del catálogo (módulo 3) y el logo del negocio (módulo 2).
 */

/**
 * Lee un archivo del equipo como data URL (`data:image/png;base64,…`).
 *
 * Solo existe en el navegador: `FileReader` no está en Node (los scripts de humo usan
 * `base64FromDataUrl` con un data URL escrito a mano, no este helper).
 */
export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Parte un data URL (`data:image/png;base64,iVBOR…`) en su contenido y su MIME.
 *
 * El backend acepta las dos formas, pero mandar el base64 pelado y el `mimeType` aparte deja el
 * body explícito (y no depende de que el prefijo sea el que el backend espera). Si el texto no
 * viene con prefijo, se devuelve tal cual y sin MIME: el backend lo detecta por firma binaria.
 */
export function base64FromDataUrl(dataUrl: string): { base64: string; mimeType: string | null } {
  const match = /^data:([^;,]+)?;base64,([\s\S]*)$/.exec(dataUrl.trim());
  if (!match) return { base64: dataUrl.trim(), mimeType: null };

  return { base64: (match[2] ?? '').trim(), mimeType: match[1] ?? null };
}

/* ---------------------------------------------------------------------------
 * Topes de las imágenes que sube el dashboard
 * ------------------------------------------------------------------------- */

/**
 * Topes comunes a TODA imagen que se sube al backend: la foto de la guía de envío (módulo 7), las
 * del catálogo (módulo 3) y el logo del negocio (módulo 2, Ajustes → General).
 *
 * Espejo de `backend/src/lib/local-storage.ts` + `lib/image-download.ts`: 5 MB es el límite de
 * WhatsApp Cloud API y los formatos son los que el backend reconoce por firma binaria. Viven aquí
 * (y no dentro de un módulo) para no repetir la regla en cada subida.
 */
export const IMAGE_MAX_BYTES = 5 * 1024 * 1024;
export const IMAGE_FORMATS_LABEL = 'JPEG, PNG, WEBP, GIF o HEIC';
export const IMAGE_MIME_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'image/heic',
] as const;

export type ImageMimeType = (typeof IMAGE_MIME_TYPES)[number];

/* ---------------------------------------------------------------------------
 * Fotos del CATÁLOGO: tope y reescalado antes de subir
 * ------------------------------------------------------------------------- */

/**
 * Tope de una foto del catálogo (2 MB): espejo de `MAX_PRODUCT_IMAGE_BYTES`
 * (`backend/src/lib/local-storage.ts`).
 *
 * El catálogo acepta hasta 20 fotos POR PRODUCTO, así que es la subida que de verdad llena el disco
 * del servidor (y sus respaldos). La guía de envío y el logo siguen con `IMAGE_MAX_BYTES` (5 MB):
 * son una sola imagen por orden/negocio.
 */
export const PRODUCT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;

/** Lado máximo (px) al que el panel reescala una foto del catálogo antes de subirla. */
export const PRODUCT_IMAGE_MAX_SIDE = 1600;

/** Calidad del JPEG que sale del canvas (0-1): 0.82 se ve bien y pesa ~5 veces menos que el original. */
export const PRODUCT_IMAGE_QUALITY = 0.82;

/** Archivo que, pesando más que esto, se reescala aunque ya esté dentro del lado máximo. */
export const PRODUCT_IMAGE_RESIZE_THRESHOLD_BYTES = 400 * 1024;

/**
 * Formatos que NO se reescalan: el canvas no los sabe dibujar (HEIC en casi todos los navegadores) o
 * perdería la animación (GIF). Se suben tal cual, como se hacía antes de existir este paso.
 */
const SIN_REESCALADO = ['image/gif', 'image/heic'];

/**
 * Valida el archivo elegido en el equipo, ANTES de leerlo (así no se lee un archivo de 20 MB para
 * que el backend lo rechace después). Solo mira tamaño y MIME declarado; el formato real lo confirma
 * el backend por firma binaria. El MIME vacío se acepta a propósito (algunos sistemas no lo
 * declaran, p. ej. HEIC).
 *
 * `maxBytes` permite un tope PROPIO de la pantalla (las fotos del catálogo pasan
 * `PRODUCT_IMAGE_MAX_BYTES`, que es más bajo que el de WhatsApp); por defecto se usa el general.
 */
export function validateImageFile(
  file: {
    name: string;
    size: number;
    type: string;
  },
  maxBytes: number = IMAGE_MAX_BYTES,
): string | undefined {
  if (file.size === 0) return `«${file.name}» está vacío`;

  if (file.size > maxBytes) {
    const max = Math.round(maxBytes / 1024 / 1024);
    return `«${file.name}» supera el máximo de ${max} MB`;
  }

  const mimeType = file.type.trim().toLowerCase();
  if (mimeType.length > 0 && !IMAGE_MIME_TYPES.includes(mimeType as ImageMimeType)) {
    return `«${file.name}» no es una imagen soportada (${IMAGE_FORMATS_LABEL})`;
  }

  return undefined;
}

/**
 * Escala a la que se debe dibujar una imagen para que su lado mayor quede en `maxSide`.
 *
 * Nunca agranda (una foto de 800 px se queda en 800) y mantiene la proporción. Es PURA a propósito:
 * el smoke la prueba en Node, donde no hay `Image` ni `canvas`.
 */
export function escalaObjetivo(
  width: number,
  height: number,
  maxSide: number = PRODUCT_IMAGE_MAX_SIDE,
): { width: number; height: number; redimensionada: boolean } {
  if (width <= maxSide && height <= maxSide) {
    return { width, height, redimensionada: false };
  }

  const factor = maxSide / Math.max(width, height);

  return {
    width: Math.max(1, Math.round(width * factor)),
    height: Math.max(1, Math.round(height * factor)),
    redimensionada: true,
  };
}

/** Carga un data URL como imagen del DOM (solo navegador). */
function cargarImagen(dataUrl: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('No se pudo leer la imagen'));
    image.src = dataUrl;
  });
}

/**
 * Prepara una foto del catálogo para subirla: la REESCALA en el navegador y devuelve el base64.
 *
 * Por qué se hace acá y no en el backend: el backend no tiene `sharp` (ni ninguna librería de
 * imágenes) y el archivo igual tiene que viajar en base64 dentro del JSON. Reescalar en el navegador
 * es gratis, no agrega dependencias y hace el viaje 5-10 veces más liviano: una foto de cámara de
 * 4 MB y 4000 px queda en ~300 KB y 1600 px, que es lo que se ve bien en WhatsApp.
 *
 * Reglas: PNG conserva su canal alfa (se re-codifica como PNG), JPEG/WEBP salen como JPEG con
 * `PRODUCT_IMAGE_QUALITY`, y GIF/HEIC se suben tal cual (el canvas no los puede dibujar o perdería
 * la animación). Si algo falla (imagen corrupta, canvas sin contexto), se devuelve el original: el
 * backend sigue validando, así que el peor caso es el de siempre.
 */
export async function prepararFotoProducto(
  file: File,
): Promise<{ base64: string; mimeType: string | null }> {
  const original = base64FromDataUrl(await readFileAsDataUrl(file));
  const mimeType = (original.mimeType ?? file.type).trim().toLowerCase();

  if (SIN_REESCALADO.includes(mimeType)) return original;

  try {
    const image = await cargarImagen(`data:${mimeType || 'image/jpeg'};base64,${original.base64}`);
    const escala = escalaObjetivo(image.naturalWidth, image.naturalHeight);
    const valeLaPena = escala.redimensionada || file.size > PRODUCT_IMAGE_RESIZE_THRESHOLD_BYTES;
    if (!valeLaPena) return original;

    const canvas = document.createElement('canvas');
    canvas.width = escala.width;
    canvas.height = escala.height;

    const contexto = canvas.getContext('2d');
    if (!contexto) return original;

    contexto.drawImage(image, 0, 0, escala.width, escala.height);

    const salida = mimeType === 'image/png' ? 'image/png' : 'image/jpeg';
    return base64FromDataUrl(canvas.toDataURL(salida, PRODUCT_IMAGE_QUALITY));
  } catch {
    return original;
  }
}

/**
 * Prepara la foto que se manda desde el chat del Inbox (módulo 5): el MISMO trabajo que
 * `prepararFotoProducto` —validar en el llamador y reescalar a 1600 px con calidad 0.82— y el mismo
 * tope de 2 MB.
 *
 * Se expone con su propio nombre porque el caso de uso no es el catálogo: la foto del chat se manda
 * una vez y se queda en `uploads/chat/`, así que los topes que aplican son los del catálogo
 * (`MAX_PRODUCT_IMAGE_BYTES`) y no los de WhatsApp (5 MB). Es un alias a propósito: duplicar el
 * reescalado sería tener dos verdades sobre lo mismo.
 */
export const prepararFotoChat = prepararFotoProducto;
